import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { DateTime } from 'luxon';

import {
  formatColumnDate,
  dateFilterParams,
  getValueBgClass,
  formatLongPrice,
  getPureNumber,
  headerInfoTooltipDef,
  transformPercentageValue,
  formatPercentage,
} from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const BillionsFilter = [
  {
    label: '$0 to $100M',
    rules: (value: number) => {
      return value >= 0 && value <= 100000000;
    },
  },
  {
    label: '$101M to $500M',
    rules: (value: number) => {
      return value >= 101000000 && value <= 500000000;
    },
  },
  {
    label: '$501M to $1B',
    rules: (value: number) => {
      return value >= 5010000000 && value <= 1000000000;
    },
  },
  {
    label: 'Greater than 1B',
    rules: (value: number) => {
      return value >= 1000000000;
    },
  },
  {
    label: '$0 to -$100M',
    rules: (value: number) => {
      return value <= 0 && value >= -100000000;
    },
  },
  {
    label: '-$101M to -$500M',
    rules: (value: number) => {
      return value <= -101000000 && value >= -500000000;
    },
  },
  {
    label: '-$501M to -$1B',
    rules: (value: number) => {
      return value <= -5010000000 && value >= -1000000000;
    },
  },
  {
    label: 'Less than -$1B',
    rules: (value: number) => {
      return value <= -1000000000;
    },
  },
];

export default [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Date',
    initialWidth: 100,
    minWidth: 100,
    sort: 'desc',
    sortIndex: 0,
    valueFormatter: ({ data }) => formatColumnDate(data.date),
    valueGetter: ({ data }) => {
      if (!data.date) return null;

      return data.date;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'time',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      values: [
        {
          label: 'AM',
          rules: (value: number) => {
            return DateTime.fromJSDate(new Date(value)).hour < 13;
          },
        },
        {
          label: 'PM',
          rules: (value: number) => {
            return DateTime.fromJSDate(new Date(value)).hour >= 13;
          },
        },
      ],
    },
    headerName: 'Time',
    initialWidth: 100,
    minWidth: 100,
    sort: 'desc',
    sortIndex: 1,
    valueFormatter: ({ value }) => {
      if (!value) return '';

      return DateTime.fromJSDate(value).toFormat('h:mm a');
    },
    valueGetter: ({ data }) => {
      if (!data.time) return '';

      return DateTime.fromFormat(data.time, 'HH:mm:ss').toJSDate();
    },
  },
  {
    cellClass: 'ticker-cell country-cell',
    cellRenderer: ({ data }: { data: any }) => {
      return (
        <>
          <div
            className="country-flag-icon mr-[0.3125rem] inline-block align-middle h-3 w-[17px] border border-[#d6d6d6] bg-white bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(https://www.benzinga.com/wp-content/themes/benzinga-money/dist/rebuild/images/icons/countries-flags/alpha3/${data.country}.svg)`,
            }}
          />
          {data.country}
        </>
      );
    },
    field: 'country',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 80,
    minWidth: 80,
    valueFormatter: ({ data }) => {
      return data.country;
    },
  },
  {
    cellClass: 'ticker-cell',
    field: 'event_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Econ Event',
    initialWidth: 200,
    minWidth: 180,
  },
  {
    cellClass: 'ticker-cell',
    field: 'event_period',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Period',
    initialWidth: 70,
    minWidth: 70,
    valueGetter: ({ data }) => {
      if (!data.event_period && !data.period_year) return '-';

      return `${data.event_period} ${data.period_year}`;
    },
  },
  {
    cellClass: ({ value }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'prior',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: BillionsFilter,
    },
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
    valueGetter: ({ data }) => getPureNumber(data.prior),
  },
  {
    cellClass: 'ticker-cell',
    field: 'consensus',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: BillionsFilter,
    },
    headerName: 'Forecast',
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
    valueGetter: ({ data }) => getPureNumber(data.consensus),
  },
  {
    cellClass: 'ticker-cell',
    cellStyle: { fontWeight: '900' },
    field: 'actual',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: BillionsFilter,
    },
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ value }) => formatLongPrice(value, ''),
    valueGetter: ({ data }) => getPureNumber(data.actual),
  },
  {
    cellClass: ({ value }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    field: 'surprise',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: [
        {
          label: '0% - 5%',
          rules: (value: number) => {
            return value >= 0 && value <= 5;
          },
        },
        {
          label: '6% - 10%',
          rules: (value: number) => {
            return value >= 6 && value <= 10;
          },
        },
        {
          label: '11% - 15%',
          rules: (value: number) => {
            return value >= 11 && value <= 15;
          },
        },
        {
          label: '16% - 20%',
          rules: (value: number) => {
            return value >= 16 && value <= 20;
          },
        },
        {
          label: 'Greater than 20%',
          rules: (value: number) => {
            return value >= 20;
          },
        },
      ],
    },
    headerName: 'Surprise',
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ value }) => formatPercentage(value),
    valueGetter: ({ data }) => {
      if (data.consensus === '' || data.consensus === undefined || data.actual === '' || data.actual === undefined) {
        return null;
      }

      const value = (data.consensus - data.actual) / data.consensus;

      return transformPercentageValue(value);
    },
  },
  {
    cellClass: 'importance-cell',
    field: 'importance',
    headerName: 'Imp.',
    ...headerInfoTooltipDef('Importance'),
    cellRenderer: ({ data }: { data: any }) => {
      const importanceLevelPercentage = 100 / (5 / Number(data.importance)) || 0;

      return (
        <div className="flex items-center justify-center w-[80px]">
          <span
            className="importance-indicator flex items-center w-full bg-blue-100 rounded h-5 border border-blue-200"
            role="presentation"
          >
            <div
              className="importance-indicator-value h-[18px] rounded text-white flex items-end justify-end pr-1"
              style={{
                background: 'linear-gradient(270deg, #1a79ff 0%, #97c0f9 100%)',
                width: `${importanceLevelPercentage}%`,
              }}
            >
              <span className="ml-2 inline-block">{data.importance}</span>
            </div>
          </span>
        </div>
      );
    },
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 94,
    minWidth: 94,
  },

  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    minWidth: 70,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
